/**
 * "Use my current location" for the address map picker: permission,
 * location-services and error handling, kept free of React so it can be
 * unit-tested.
 *
 * Runs on @capacitor/geolocation, which covers both runtimes:
 *  - Native app (Capacitor): shows the OS permission prompt. On Android the
 *    plugin can also show the system "turn on location" dialog.
 *  - Browser: falls back to navigator.geolocation; the browser prompts.
 *
 * Automatic attempts (the picker opening) never re-ask after a refusal — only
 * an explicit tap does — so a denied permission can't turn into a prompt or
 * error loop. Always resolves to { status, coords? }; never throws.
 */

export const LocationStatus = Object.freeze({
  OK: 'ok',
  DENIED: 'denied', // refused, but the app may ask again
  BLOCKED: 'blocked', // refused for good ("Don't ask again", or blocked in the browser) — only Settings can undo it
  SERVICES_OFF: 'services_off', // the device's location / GPS is switched off
  UNSUPPORTED: 'unsupported', // this app build can't provide location (built without the location plugin/permission)
  UNAVAILABLE: 'unavailable', // no position could be determined
  TIMEOUT: 'timeout',
});

const S = LocationStatus;

// @capacitor/geolocation error codes (see its README "Errors" table).
const SERVICES_OFF_CODES = new Set([
  'OS-PLUG-GLOC-0007', // location services are not enabled
  'OS-PLUG-GLOC-0009', // user declined Android's "turn on location" dialog
  'OS-PLUG-GLOC-0017', // both network and location are off
]);
const UNSUPPORTED_CODES = new Set([
  'UNIMPLEMENTED', // the native plugin isn't in this app build
  'OS-PLUG-GLOC-0018', // location permissions missing from AndroidManifest.xml
]);
const PERMISSION_DENIED_CODE = 'OS-PLUG-GLOC-0003';
const RESTRICTED_CODE = 'OS-PLUG-GLOC-0008'; // iOS: restricted by parental controls / device management
const TIMEOUT_CODE = 'OS-PLUG-GLOC-0010';

// Browser GeolocationPositionError codes.
const WEB_PERMISSION_DENIED = 1;
const WEB_TIMEOUT = 3;

const isGranted = (state) => state === 'granted' || state === 'limited';

/**
 * Reads a Geolocation.checkPermissions() result. On Android "approximate
 * location" grants only coarseLocation, which is still enough to locate.
 */
export function readPermission(result) {
  const location = result?.location;
  const coarse = result?.coarseLocation;
  if (isGranted(location)) return { state: 'granted', precise: true };
  if (isGranted(coarse)) return { state: 'granted', precise: false };
  const state = location ?? coarse;
  if (state === 'prompt') return { state: 'prompt' };
  if (state === 'prompt-with-rationale') return { state: S.DENIED };
  if (state === 'denied') return { state: S.BLOCKED };
  return { state: 'unknown' };
}

export function classifyLocationError(err, { native }) {
  const code = err?.code;
  if (UNSUPPORTED_CODES.has(code)) return S.UNSUPPORTED;
  if (SERVICES_OFF_CODES.has(code)) return S.SERVICES_OFF;
  if (code === RESTRICTED_CODE) return S.BLOCKED;
  if (code === PERMISSION_DENIED_CODE) return S.DENIED;
  if (code === TIMEOUT_CODE) return S.TIMEOUT;
  if (!native && code === WEB_PERMISSION_DENIED) return S.DENIED;
  if (!native && code === WEB_TIMEOUT) return S.TIMEOUT;
  return S.UNAVAILABLE;
}

const toCoords = (pos) => ({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });

/**
 * @param deps.geolocation     the @capacitor/geolocation plugin (or a stand-in)
 * @param deps.native          running inside the native app
 * @param deps.pluginAvailable the native Geolocation plugin exists in this build
 * @param options.interactive  the user explicitly tapped "use current location"
 */
export async function locateCurrentPosition(
  { geolocation, native = false, pluginAvailable = true, timeout = 15000 },
  { interactive = false } = {},
) {
  if (native && !pluginAvailable) return { status: S.UNSUPPORTED };

  let permission = { state: 'unknown' };
  try {
    permission = readPermission(await geolocation.checkPermissions());
  } catch (err) {
    const status = classifyLocationError(err, { native });
    if (status === S.UNSUPPORTED) return { status };
    // Location switched off: the permission state can't even be read. On a tap,
    // getCurrentPosition below asks for permission if needed and then shows
    // Android's own "turn on location" dialog. On the automatic attempt, don't
    // pop dialogs at the user — report it and let them choose.
    if (status === S.SERVICES_OFF && !interactive) return { status };
    // Otherwise (e.g. a browser without the Permissions API) just try —
    // getCurrentPosition prompts by itself.
  }

  if (!interactive && (permission.state === S.DENIED || permission.state === S.BLOCKED)) {
    return { status: permission.state };
  }

  try {
    // Only ask for high accuracy when precise location is (or may be) allowed:
    // with "approximate" granted on Android 12+, high accuracy makes the plugin
    // re-prompt for precise location on every attempt.
    const pos = await geolocation.getCurrentPosition({
      enableHighAccuracy: permission.precise !== false,
      timeout,
      maximumAge: 0,
    });
    return { status: S.OK, coords: toCoords(pos) };
  } catch (err) {
    const status = classifyLocationError(err, { native });
    if (status !== S.DENIED) return { status };
    // Refused (just now or earlier): tell "may ask again" from "Settings only".
    try {
      if (readPermission(await geolocation.checkPermissions()).state === S.BLOCKED) {
        return { status: S.BLOCKED };
      }
    } catch (_) { /* keep DENIED */ }
    return { status: S.DENIED };
  }
}

const MANUAL_HINT = 'You can still search or tap on the map to set your address.';

/**
 * What to tell the user for a failed attempt, and which single action to offer:
 * 'retry' (ask again / try again), 'app_settings' (this app's settings page),
 * 'location_settings' (the device's location settings), or none.
 *
 * @param ctx.native          running inside the native app
 * @param ctx.platform        'android' | 'ios' | 'web'
 * @param ctx.canOpenSettings the native AppSettings plugin exists in this build
 * @param ctx.interactive     the failed attempt was an explicit tap
 */
export function locationNotice(status, { native = false, platform = 'web', canOpenSettings = false, interactive = false } = {}) {
  switch (status) {
    case S.DENIED:
      return native
        ? { title: 'Location permission is off', message: `Allow location access to find your address automatically. ${MANUAL_HINT}`, action: 'retry', actionLabel: 'Allow location' }
        : { title: 'Location access was not allowed', message: `Allow location for this site in your browser, then try again. ${MANUAL_HINT}`, action: 'retry', actionLabel: 'Try again' };

    case S.BLOCKED:
      if (!native) {
        return { title: 'Location is blocked for this site', message: `Allow location in your browser's site settings, then try again. ${MANUAL_HINT}`, action: 'retry', actionLabel: 'Try again' };
      }
      if (canOpenSettings) {
        return { title: 'Location permission is blocked', message: `Tap Open Settings → Permissions → Location, then allow it. ${MANUAL_HINT}`, action: 'app_settings', actionLabel: 'Open Settings' };
      }
      return {
        title: 'Location permission is blocked',
        message: platform === 'ios'
          ? `Turn it on in your iPhone's Settings → this app → Location. ${MANUAL_HINT}`
          : `Turn it on in your phone's Settings → Apps → this app → Permissions → Location. ${MANUAL_HINT}`,
        action: 'retry',
        actionLabel: 'Try again',
      };

    case S.SERVICES_OFF:
      // After a tap has already offered Android's "turn on location" dialog,
      // send them to the location settings screen instead of repeating it.
      if (interactive && canOpenSettings) {
        return { title: "Your phone's location is off", message: `Turn on Location (GPS) to use your current position. ${MANUAL_HINT}`, action: 'location_settings', actionLabel: 'Open Settings' };
      }
      return { title: "Your phone's location is off", message: `Turn on Location (GPS) to use your current position. ${MANUAL_HINT}`, action: 'retry', actionLabel: native ? 'Turn on location' : 'Try again' };

    case S.UNSUPPORTED:
      return { title: "Current location isn't available in this app version", message: `Update the app to use your current location. ${MANUAL_HINT}`, action: null };

    case S.TIMEOUT:
      return { title: 'Finding your location is taking too long', message: `Move to an open area and try again. ${MANUAL_HINT}`, action: 'retry', actionLabel: 'Try again' };

    case S.UNAVAILABLE:
      return { title: "Couldn't get your current location", message: `Check that location is on and try again. ${MANUAL_HINT}`, action: 'retry', actionLabel: 'Try again' };

    default:
      return null;
  }
}
